import { THEME_DATA_ATTRIBUTES, areModesEqual, dataAttributesFromTheme, themeFromDocument } from "../utils/theme.js";
import { ThemeColorContext } from "../hooks/use-color-theme.js";
import { ThemeContext, useIsNestedThemeContext } from "../hooks/use-theme.js";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/theme-provider/theme-provider.tsx
/**
* Manages the following data attributes on the document:
*
* | Attribute                | Description                                       | Owner         |
* |--------------------------|---------------------------------------------------|--------------:|
* | `data-preferred-theme`   | The color theme, e.g. light / dark                | body          |
* | `data-editor-theme`      | The editor brand, e.g. design / whiteboard / etc. | body          |
* | `data-enhanced-contrast` | Is enhanced contrast mode enabled                 | body          |
*/
function ThemeProvider({ children }) {
	const [theme, setTheme] = useState(themeFromDocument);
	if (useIsNestedThemeContext()) console.error("Only one instance of theme provider should exist");
	const listeners = useRef(/* @__PURE__ */ new Set());
	const addThemeListener = useCallback((listener) => {
		listeners.current.add(listener);
		return () => listeners.current.delete(listener);
	}, []);
	const colorContext = useMemo(() => ({
		...theme,
		addThemeListener,
		dataAttributes: dataAttributesFromTheme(theme)
	}), [theme, addThemeListener]);
	useEffect(() => {
		const observer = new MutationObserver(() => {
			setTheme((oldMode) => {
				const newMode = {
					...oldMode,
					...themeFromDocument()
				};
				if (areModesEqual(oldMode, newMode)) return oldMode;
				for (const listener of listeners.current) listener(newMode);
				return newMode;
			});
		});
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: THEME_DATA_ATTRIBUTES
		});
		return () => observer.disconnect();
	}, []);
	useLayoutEffect(() => {
		document.body.setAttribute("data-fpl-version", "ui3");
	}, []);
	return /* @__PURE__ */ jsx(ThemeContext, {
		value: true,
		children: /* @__PURE__ */ jsx(ThemeColorContext, {
			value: colorContext,
			children
		})
	});
}
//#endregion
export { ThemeProvider };

//# sourceMappingURL=theme-provider.js.map